import { EventEmitter, Injectable, OnDestroy } from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Subscription } from "rxjs";
import {
  DealerClient,
  DealerViewModel,
  ModelClient,
  ModelGroupViewModel,
  ModelViewModel,
  SearchDealerRequestModel,
} from "src/domain/client";
import { BrandService } from "./brand.service";
import { nonNullish } from "src/guards";
import { ErrorReporterService } from "./error-reporter.service";
import { ObservableProps } from "src/mixins/ObservableProps";

@Injectable({
  providedIn: "root",
})
export class ModelAndDealerService
  extends ObservableProps<{
    years: number[];
    modelGroups: ModelGroupViewModel[];
    models: ModelViewModel[];
    dealers: DealerViewModel[];
    presetDealers: string[];
    selectedDealer: DealerViewModel | null;
    selectedModel: ModelViewModel | null;
  }>
  implements OnDestroy
{
  dealerSearchInput?: string;
  openSelectDealer$ = new EventEmitter<void>();

  private onDestroySubscription: Subscription;

  modelForm = new FormGroup({
    year: new FormControl(-1, Validators.required),
    modelGroupId: new FormControl(-1, Validators.required),
    model: new FormControl(null, Validators.required),
  });

  constructor(
    private errorReporter: ErrorReporterService,
    private brandService: BrandService,
  ) {
    const onDestroySubscription = new Subscription();
    super({
      years: [],
      modelGroups: [],
      models: [],
      dealers: [],
      presetDealers: [],
      selectedDealer: null,
      selectedModel: null,
    }, onDestroySubscription);
    this.onDestroySubscription = onDestroySubscription;
    this.initFormSubscribers();
  }

  ngOnDestroy(): void {
    this.onDestroySubscription.unsubscribe();
  }

  getModelGroups(): void {
    new ModelClient()
      .groups(this.brandService.brandId)
      .then((modelGroups) => (this.modelGroups = modelGroups))
      .catch(this.errorReporter.handle);
  }

  getYears(year: number): void {
    new ModelClient()
      .years(this.brandService.brandId, year)
      .then((years) => {
        this.years = years;
      })
      .catch(this.errorReporter.handle);
  }

  getModels(modelGroupId: number, year: number): void {
    new ModelClient()
      .model(this.brandService.brandId, year, modelGroupId)
      .then((models) => (this.models = models))
      .catch(this.errorReporter.handle);
  }

  getModelByCodeAndYear(modelCode: string, year: number) {
    return new ModelClient().code(this.brandService.brandId, modelCode, year);
  }

  getDealers(
    requestModel: SearchDealerRequestModel,
    store = true,
  ): Promise<DealerViewModel[]> {
    return new DealerClient().dealer(requestModel).then((dealers) => {
      if (this.presetDealers?.length > 0) {
        dealers = this.presetDealers
          .map((presetDealer) =>
            dealers.find((dealer) => dealer.number === presetDealer),
          )
          .filter(nonNullish);
      }
      if (store) {
        this.dealers = dealers;
      }
      return dealers;
    });
  }

  setSelectedDealer(dealer: DealerViewModel | null): void {
    this.selectedDealer = dealer;
  }

  setSelectedModel(model: ModelViewModel | null): void {
    this.selectedModel = model;
  }

  setPresetDealers(dealerNumbers: string[]): void {
    this.presetDealers = dealerNumbers;
  }

  openSelectDealer(value?: any): void {
    if (!!this.dealerSearchInput && this.dealerSearchInput === value) {
      this.dealerSearchInput = "";
    } else {
      this.dealerSearchInput = value;
    }
    this.openSelectDealer$.emit(value);
  }

  //////////

  private initFormSubscribers(): void {
    // get years when model group changes
    this.onDestroySubscription.add(
      this.modelForm.get("modelGroupId")?.valueChanges.subscribe((val) => {
        this.modelForm.patchValue({
          year: -1,
        });
        this.resetYears();
        this.resetModels();

        if (val) {
          this.getYears(val);
        }
      }),
    );

    // get models when year
    this.onDestroySubscription.add(
      this.modelForm.get("year")?.valueChanges.subscribe((year) => {
        this.resetModels();
        const modelGroupId = this.modelForm.value.modelGroupId;
        if (modelGroupId && year) {
          this.getModels(modelGroupId, year);
        }
      }),
    );
  }

  private resetYears(): void {
    this.years = [];
  }

  private resetModels(): void {
    this.models = [];
  }
}
